import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getTeamMembers } from '../../utils/teamUtils';
import AdminStatsCard from '../../components/admin/AdminStatsCard';
import { calcCommission } from '../../features/commissions/utils/commissionCalculator';

export default function TeamLeaderDashboard() {
  const { currentAgent } = useAuth();
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamDemos, setTeamDemos] = useState([]);
  const [teamOrders, setTeamOrders] = useState([]);
  
  // Modal
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showAgentModal, setShowAgentModal] = useState(false);
  
  // Commission Calculator Modal
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [commissionSales, setCommissionSales] = useState('');
  const [commissionAgentType, setCommissionAgentType] = useState('TM');
  const [calculatedCommission, setCalculatedCommission] = useState(null);
  
  // Commission Comparison Data
  const [commissionComparison, setCommissionComparison] = useState([]);
  
  const [stats, setStats] = useState({
    totalTeamMembers: 0,
    totalDemos: 0,
    upcomingDemos: 0,
    totalOrders: 0
  });

  // Load team data
  useEffect(() => {
    const loadTeamData = async () => {
      if (!currentAgent?.agent_id) return;
      
      try {
        setLoading(true);
        
        // Get team members
        const members = await getTeamMembers(currentAgent.agent_id);
        setTeamMembers(members);
        
        if (members.length === 0) {
          setStats({
            totalTeamMembers: 0,
            totalDemos: 0,
            upcomingDemos: 0,
            totalOrders: 0
          });
          setLoading(false);
          return;
        }
        
        // Load demos for team
        const demosRef = collection(db, 'demo_bookings');
        const demoPromises = [];
        
        // Query demos for agents (by payload.agentCode)
        const agentIds = members.map(m => m.agent_id);
        for (let i = 0; i < agentIds.length; i += 10) {
          const batch = agentIds.slice(i, i + 10);
          const q = query(demosRef, where('payload.agentCode', 'in', batch));
          demoPromises.push(getDocs(q));
        }
        
        const demoResults = await Promise.all(demoPromises);
        const allDemos = [];
        demoResults.forEach(snapshot => {
          snapshot.docs.forEach(doc => {
            allDemos.push({ id: doc.id, ...doc.data() });
          });
        });
        
        // Sort demos by date (descending) - use updatedAt if available, otherwise demoDate
        allDemos.sort((a, b) => {
          // Helper function to get the most relevant date for a demo
          const getDemoDate = (demo) => {
            // Try updatedAt first (for processed demos)
            if (demo.payload?.updatedAt) {
              const updatedAt = demo.payload.updatedAt;
              // Handle Firestore Timestamp
              if (updatedAt && typeof updatedAt.toDate === 'function') {
                return updatedAt.toDate();
              }
              // Handle string timestamp
              if (typeof updatedAt === 'string') {
                return new Date(updatedAt);
              }
              // Handle timestamp object
              if (updatedAt && updatedAt.seconds) {
                return new Date(updatedAt.seconds * 1000);
              }
            }
            
            // Fall back to demo date
            const demoDate = demo.payload?.dateTime?.demoDate;
            if (demoDate) {
              return new Date(demoDate);
            }
            
            // Default to old date
            return new Date('1970-01-01');
          };
          
          const dateA = getDemoDate(a);
          const dateB = getDemoDate(b);
          
          // Sort descending (newest first)
          return dateB.getTime() - dateA.getTime();
        });
        
        // Load orders for team
        const ordersRef = collection(db, 'orders');
        const orderPromises = [];
        
        // Use document IDs (full_agent_id) for order queries
        const fullAgentIds = members.map(m => m.full_agent_id);
        // Include team leader's own document ID
        const allAgentIds = [...new Set([...fullAgentIds, currentAgent.id])];
        
        // Query orders for agents (by agent_id field only)
        for (let i = 0; i < allAgentIds.length; i += 10) {
          const batch = allAgentIds.slice(i, i + 10);
          const q = query(ordersRef, where('agent_id', 'in', batch));
          orderPromises.push(getDocs(q));
        }
        
        const orderResults = await Promise.all(orderPromises);
        const allOrders = [];
        orderResults.forEach(snapshot => {
          snapshot.docs.forEach(doc => {
            allOrders.push({ id: doc.id, ...doc.data() });
          });
        });
        
        // Sort orders by date
        allOrders.sort((a, b) => {
          const dateA = a.created_at?.toDate?.() || new Date(a.created_at);
          const dateB = b.created_at?.toDate?.() || new Date(b.created_at);
          return dateB - dateA;
        });
        
        // Calculate stats
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const upcomingDemos = allDemos.filter(demo => {
          const demoDate = new Date(demo.payload?.dateTime?.demoDate);
          return demoDate >= today;
        });
        
        setStats({
          totalTeamMembers: members.length,
          totalDemos: allDemos.length,
          upcomingDemos: upcomingDemos.length,
          totalOrders: allOrders.length
        });
        
        setTeamDemos(allDemos);
        setTeamOrders(allOrders);
        
        // Load commission comparison data
        await loadCommissionComparison(members);
        
      } catch (error) {
        console.error('Error loading team data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    const loadCommissionComparison = async (members) => {
      try {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;
        
        // Calculate previous month
        const prevDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const prevYear = prevDate.getFullYear();
        const prevMonth = prevDate.getMonth() + 1;
        
        const comparisonData = [];
        
        for (const member of members) {
          const agentId = member.full_agent_id;
          
          // Fetch previous month's commission from Firestore
          const prevPeriod = `${prevYear}-${String(prevMonth).padStart(2, '0')}`;
          const prevDocId = `${agentId}_${prevPeriod}`;
          const prevCommissionRef = doc(db, 'commissions', prevDocId);
          const prevDocSnap = await getDoc(prevCommissionRef);
          
          const prevCommission = prevDocSnap.exists() 
            ? prevDocSnap.data().totalCommission || 0 
            : 0;
          
          // Calculate current month's commission from orders
          const currentMonthStart = new Date(Date.UTC(currentYear, currentMonth - 1, 1, 0, 0, 0));
          const currentMonthEnd = new Date(Date.UTC(currentYear, currentMonth, 0, 23, 59, 59, 999));
          
          const ordersRef = collection(db, 'orders');
          const ordersSnapshot = await getDocs(ordersRef);
          
          let currentMonthSalesUSD = 0;
          ordersSnapshot.forEach((doc) => {
            const orderData = doc.data();
            const orderAgentId = orderData.agent_id || orderData.agent?.agent_id || orderData.agent?.id;
            
            if (orderAgentId !== agentId) return;
            
            let orderDate;
            if (orderData.created_at) {
              if (orderData.created_at.toDate) {
                orderDate = orderData.created_at.toDate();
              } else if (typeof orderData.created_at === 'string') {
                orderDate = new Date(orderData.created_at);
              } else if (orderData.created_at.seconds) {
                orderDate = new Date(orderData.created_at.seconds * 1000);
              } else {
                orderDate = new Date(orderData.created_at);
              }
            } else {
              return;
            }
            
            const orderDateUTC = new Date(Date.UTC(
              orderDate.getFullYear(),
              orderDate.getMonth(),
              orderDate.getDate(),
              orderDate.getHours(),
              orderDate.getMinutes(),
              orderDate.getSeconds()
            ));
            
            if (orderDateUTC >= currentMonthStart && orderDateUTC <= currentMonthEnd) {
              const usdAmount = parseFloat(orderData.subtotal_usd || 0);
              currentMonthSalesUSD += usdAmount;
            }
          });
          
          // Calculate current month commission using existing utility
          const isTeamLeader = member.role === 'admin' || member.role === 'teamLeader';
          const commissionType = isTeamLeader ? 'TL' : 'TM';
          const currentCommission = calcCommission(currentMonthSalesUSD, commissionType);
          
          // Calculate override for current month (only for team members)
          const override = !isTeamLeader ? calcCommission(currentMonthSalesUSD, 'OV') : 0;
          
          comparisonData.push({
            agentId: member.agent_id,
            name: member.name,
            previousCommission: prevCommission,
            currentCommission: currentCommission,
            override: override
          });
        }
        
        // Sort by current commission (descending) and take top agents
        comparisonData.sort((a, b) => b.currentCommission - a.currentCommission);
        setCommissionComparison(comparisonData.slice(0, 10));
        
      } catch (error) {
        console.error('Error loading commission comparison:', error);
      }
    };
    
    loadTeamData();
  }, [currentAgent?.agent_id]);


  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    const num = parseFloat(amount);
    if (isNaN(num)) return 'N/A';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatTime = (timeSlot) => {
    if (!timeSlot) return 'No time set';
    
    // Canonical format mapping
    const timeMap = {
      'Morning': 'Morning (9-12)',
      'Afternoon': 'Afternoon (1-5)',
      'Evening': 'Evening (6-9)'
    };
    
    // If it's already in canonical format, return the formatted version
    if (timeMap[timeSlot]) {
      return timeMap[timeSlot];
    }
    
    // Normalize legacy/incorrect formats to canonical format
    const normalizeMap = {
      '9:00 AM - 12:00 PM': 'Morning (9-12)',
      '1:00 PM - 4:00 PM': 'Afternoon (1-5)',
      '5:00 PM - 8:00 PM': 'Evening (6-9)'
    };
    
    return normalizeMap[timeSlot] || timeSlot;
  };

  const handleCalculateCommission = () => {
    const sales = parseFloat(commissionSales);
    if (isNaN(sales) || sales < 0) {
      setCalculatedCommission(null);
      return;
    }
    
    let commission = 0;
    let override = 0;
    
    if (commissionAgentType === 'TM') {
      commission = calcCommission(sales, 'TM');
      override = calcCommission(sales, 'OV');
    } else if (commissionAgentType === 'TL') {
      commission = calcCommission(sales, 'TL');
      override = 0;
    }
    
    setCalculatedCommission({
      sales,
      agentType: commissionAgentType,
      commission: commission.toFixed(2),
      override: override.toFixed(2)
    });
  };

  const resetCommissionCalculator = () => {
    setCommissionSales('');
    setCommissionAgentType('TM');
    setCalculatedCommission(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">👥 Team Leader Panel</h1>
              <p className="mt-2 text-sm text-gray-600">
                Manage your team's demos and orders
              </p>
            </div>
            <button
              onClick={() => setShowCommissionModal(true)}
              className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md shadow-sm transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Calculate Commission
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <AdminStatsCard title="Team Members" value={stats.totalTeamMembers} icon="👥" />
          <AdminStatsCard title="Total Demos" value={stats.totalDemos} icon="📅" />
          <AdminStatsCard title="Upcoming Demos" value={stats.upcomingDemos} icon="⏳" />
          <AdminStatsCard title="Total Orders" value={stats.totalOrders} icon="🛒" />
        </div>

        {/* Commission Comparison Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Agents Commission Comparison</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Previous Month</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Current Month</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Override</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {commissionComparison.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-sm text-gray-500">
                      No commission data available
                    </td>
                  </tr>
                ) : (
                  commissionComparison.map((agent) => (
                    <tr key={agent.agentId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {agent.agentId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {agent.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {agent.previousCommission > 0 ? `$${formatCurrency(agent.previousCommission)}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {agent.currentCommission > 0 ? `$${formatCurrency(agent.currentCommission)}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {agent.override > 0 ? `$${formatCurrency(agent.override)}` : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Team Members Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Team Members ({teamMembers.length})</h2>
            <button
              onClick={() => window.openCreateAgentModal()}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-md shadow-sm transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Create User
            </button>
          </div>
          {teamMembers.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No team members assigned yet</p>
          ) : (
            <div className="space-y-2">
              {teamMembers.map(member => {
                const orderCount = teamOrders.filter(order => order.agent_id === member.full_agent_id).length;
                return (
                  <div 
                    key={member.id} 
                    className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                    onClick={() => {
                      setSelectedAgent({ ...member, orderCount });
                      setShowAgentModal(true);
                    }}
                  >
                    <div>
                      <div className="font-medium text-gray-900">{member.name}</div>
                      <div className="text-sm text-gray-500">{member.agent_id}</div>
                    </div>
                    <div className="text-sm text-indigo-600">View Details →</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Team Demos Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Latest Demo Bookings ({teamDemos.length})</h2>
          </div>
          
          {/* Demos Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {teamDemos.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center text-sm text-gray-500">
                      No demos found
                    </td>
                  </tr>
                ) : (
                  teamDemos.slice(0, 10).map((demo) => (
                    <tr key={demo.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(demo.payload?.dateTime?.demoDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatTime(demo.payload?.dateTime?.timeSlot)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {demo.payload?.location || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {demo.payload?.agentCode || 'N/A'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {teamDemos.length > 10 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-600">Showing 10 of {teamDemos.length} demos</p>
            </div>
          )}
        </div>

        {/* Team Orders Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Latest Orders ({teamOrders.length})</h2>
          </div>
          
          {/* Orders Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent ID</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total (USD)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {teamOrders.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center text-sm text-gray-500">
                      No orders found
                    </td>
                  </tr>
                ) : (
                  teamOrders.slice(0, 10).map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {order.code || order.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {order.agent_id || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {formatCurrency(order.subtotal_usd)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(order.created_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {teamOrders.length > 10 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-600">Showing 10 of {teamOrders.length} orders</p>
            </div>
          )}
        </div>
      </div>

      {/* Agent Details Modal */}
      {showAgentModal && selectedAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-lg font-medium text-gray-900">Agent Details</h2>
              <button 
                onClick={() => {
                  setShowAgentModal(false);
                  setSelectedAgent(null);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedAgent.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedAgent.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Agent ID</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedAgent.agent_id}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <p className="mt-1 text-sm text-gray-900 capitalize">{selectedAgent.role}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Total Orders</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedAgent.orderCount}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Commission Calculator Modal */}
      {showCommissionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-900">Commission Calculator</h2>
              <button 
                onClick={() => {
                  setShowCommissionModal(false);
                  resetCommissionCalculator();
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Agent Type
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="agentType"
                        value="TM"
                        checked={commissionAgentType === 'TM'}
                        onChange={(e) => setCommissionAgentType(e.target.value)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">Team Member</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="agentType"
                        value="TL"
                        checked={commissionAgentType === 'TL'}
                        onChange={(e) => setCommissionAgentType(e.target.value)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">Team Leader</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label htmlFor="commissionSales" className="block text-sm font-medium text-gray-700 mb-2">
                    Total Sales (USD)
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      id="commissionSales"
                      type="number"
                      value={commissionSales}
                      onChange={(e) => setCommissionSales(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleCalculateCommission()}
                      placeholder="0.00"
                      className="block w-full rounded-md border-gray-300 pl-7 pr-12 py-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      min="0"
                      step="0.01"
                    />
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                      <span className="text-gray-500 sm:text-sm">USD</span>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={handleCalculateCommission}
                  disabled={!commissionSales}
                  className="w-full inline-flex justify-center items-center px-4 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Calculate Commission
                </button>

                {calculatedCommission && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
                    <h3 className="text-sm font-semibold text-green-900 mb-3">Commission Results</h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between items-center py-1">
                        <span className="text-green-700">Agent Type:</span>
                        <span className="font-semibold text-green-900">
                          {calculatedCommission.agentType === 'TM' ? 'Team Member' : 'Team Leader'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-green-700">Total Sales:</span>
                        <span className="font-semibold text-green-900">${formatCurrency(calculatedCommission.sales)}</span>
                      </div>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-green-700">
                          {calculatedCommission.agentType === 'TM' ? 'Team Member Commission:' : 'Team Leader Commission:'}
                        </span>
                        <span className="font-semibold text-green-900">${calculatedCommission.commission}</span>
                      </div>
                      {calculatedCommission.agentType === 'TM' && (
                        <div className="flex justify-between items-center py-1">
                          <span className="text-green-700">Override Generated:</span>
                          <span className="font-semibold text-green-900">${calculatedCommission.override}</span>
                        </div>
                      )}
                      <div className="pt-3 border-t border-green-300">
                        <div className="flex justify-between items-center">
                          <span className="text-green-800 font-medium text-xs">Graduated Rates Applied:</span>
                          <span className="text-green-900 text-xs">
                            {calculatedCommission.agentType === 'TM' 
                              ? '5%-30% (Team Member), 5% (Override)' 
                              : '10%-35% (Team Leader)'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
